import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { BRAND_TIFFANY_BLUE } from '../../constants/branding';

export type ServiceAccordionItem = {
  service_id: number;
  service_name: string;
  price: number;
  duration_minutes?: number | null;
};

export type ServiceAccordionCategory<T extends ServiceAccordionItem = ServiceAccordionItem> = {
  category_id: number | null;
  category_name: string;
  color_class: string | null;
  services: T[];
};

type Props<T extends ServiceAccordionItem> = {
  categories: ServiceAccordionCategory<T>[];
  selectedServiceIds: number[];
  onToggleService: (service: T) => void;
  showDuration?: boolean;
};

const toNumberSafe = (v: unknown): number => {
  const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const money = (n?: number | null) => (n == null ? '-' : `$${Number(n).toFixed(2)}`);

const ServiceCategoryAccordion = <T extends ServiceAccordionItem>({
  categories,
  selectedServiceIds,
  onToggleService,
  showDuration = false,
}: Props<T>) => {
  const selectedCategoryKeys = useMemo(
    () =>
      categories
        .filter((category) =>
          category.services.some((service) => selectedServiceIds.includes(service.service_id)),
        )
        .map((category) => String(category.category_id ?? 'null')),
    [categories, selectedServiceIds],
  );

  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  useEffect(() => {
    if (selectedCategoryKeys.length === 0) return;
    setExpandedKeys((prev) => Array.from(new Set([...prev, ...selectedCategoryKeys])));
  }, [selectedCategoryKeys]);

  const toggleCategory = (key: string) => {
    setExpandedKeys((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key],
    );
  };

  return (
    <div className="space-y-4">
      {categories.map((category) => {
        const key = String(category.category_id ?? 'null');
        const isExpanded = expandedKeys.includes(key);
        const selectedCount = category.services.filter((service) =>
          selectedServiceIds.includes(service.service_id),
        ).length;

        return (
          <section
            key={key}
            className="overflow-hidden rounded-[28px] border border-[#d7efee] bg-white/98 shadow-[0_16px_34px_rgba(10,186,181,0.08)]"
          >
            <button
              type="button"
              onClick={() => toggleCategory(key)}
              className="flex w-full items-center gap-3 bg-[linear-gradient(180deg,rgba(244,253,252,0.98),rgba(255,255,255,0.96))] px-4 py-4 text-left"
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                {category.color_class ? (
                  <span className={`h-3 w-3 rounded-full ${category.color_class}`} />
                ) : (
                  <span className="h-3 w-3 rounded-full bg-slate-200" />
                )}
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{category.category_name}</div>
                  <div className="mt-1 text-xs text-slate-500">
                    {category.services.length} services
                    {selectedCount > 0 ? ` · ${selectedCount} selected` : ''}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {selectedCount > 0 ? (
                  <span className="rounded-full bg-[#dff8f6] px-2.5 py-1 text-xs font-semibold text-[#087f7c]">
                    {selectedCount}
                  </span>
                ) : null}
                <ChevronDown
                  className={[
                    'h-5 w-5 text-slate-400 transition-transform',
                    isExpanded ? 'rotate-180' : '',
                  ].join(' ')}
                />
              </div>
            </button>

            {isExpanded ? (
              <div className="space-y-3 border-t border-[#e2f3f2] bg-[linear-gradient(180deg,rgba(240,251,250,0.8),rgba(255,255,255,0.96))] px-3 py-3">
                {category.services.map((service) => {
                  const isSelected = selectedServiceIds.includes(service.service_id);
                  return (
                    <button
                      key={service.service_id}
                      type="button"
                      onClick={() => onToggleService(service)}
                      className={[
                        'w-full rounded-[24px] border px-4 py-4 text-left shadow-sm transition',
                        isSelected
                          ? 'border-[#88dfdb] bg-[linear-gradient(180deg,rgba(223,248,246,0.96),rgba(255,255,255,1))] text-slate-900 shadow-[0_12px_26px_rgba(10,186,181,0.14)]'
                          : 'border-slate-200 bg-white hover:border-[#bfe8e5] hover:bg-[#f8fdfd]',
                      ].join(' ')}
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className={`rounded-3xl p-3 ${isSelected ? 'text-white' : 'bg-slate-100 text-slate-700'}`}
                          style={isSelected ? { backgroundColor: BRAND_TIFFANY_BLUE } : undefined}
                        >
                          <Sparkles className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-base font-semibold">{service.service_name}</div>
                          {showDuration ? (
                            <div
                              className={`mt-1 text-sm ${isSelected ? 'text-slate-500' : 'text-slate-500'}`}
                            >
                              {toNumberSafe(service.duration_minutes)} min
                            </div>
                          ) : null}
                        </div>
                        <div className="text-right">
                          <div className="text-base font-semibold">{money(toNumberSafe(service.price))}</div>
                          <div className="mt-1">
                            {isSelected ? (
                              <span
                                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-white"
                                style={{ backgroundColor: BRAND_TIFFANY_BLUE }}
                              >
                                <Check className="h-3.5 w-3.5" />
                                Selected
                              </span>
                            ) : (
                              <ChevronRight className="ml-auto h-5 w-5 text-slate-300" />
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
};

export default ServiceCategoryAccordion;
